import React, { useEffect, useMemo, useRef } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RegistrationLayout } from "../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../RegistrationLayout/RegistrationSetupNavbar";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../../hook/useRegistrationStepFocus";
import RegistrationSetupWrapper from "../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../helper/constants";
import {
  clearRegistrationData,
  getRegistrationData,
} from "../registrationSetupStorage";
import {
  isRdUnavailable,
  shouldGoToNoProviderScreen,
} from "../registrationRdAvailability";
import { shouldShowProviderUnavailableScreen } from "../registrationEmailResume";
import { trackRegistrationNoProviderFallback } from "../../../helper/registrationAnalytics";
import { useLockBrowserHistory } from "../../../hook/useLockBrowserHistory";

const WELCOME_STEP = REGISTRATION_SETUP_STEP_META.WELCOME;

const getReferenceNumber = (rdAvailability) => {
  if (!rdAvailability || typeof rdAvailability !== "object") {
    return null;
  }
  return (
    rdAvailability.reference_number ??
    rdAvailability.reference_no ??
    rdAvailability.referenceNumber ??
    null
  );
};

const RegistrationNoRdAvailable = ({ history, location }) => {
  const { t } = useTranslation();
  const stepTitleRef = useRegistrationStepFocus();
  const fallbackTrackedRef = useRef(false);
  const registrationData = getRegistrationData();
  const rdAvailability =
    location?.state?.rdAvailability ?? registrationData.rdAvailability;
  const showFallback =
    isRdUnavailable(rdAvailability) ||
    shouldShowProviderUnavailableScreen({
      data: registrationData.emailCodeCheck,
    }) ||
    shouldGoToNoProviderScreen({ data: registrationData.emailCodeCheck }) ||
    shouldGoToNoProviderScreen({ data: rdAvailability });

  const { releaseLock } = useLockBrowserHistory(showFallback, history);

  const referenceNumber = useMemo(
    () => getReferenceNumber(rdAvailability),
    [rdAvailability]
  );

  useEffect(() => {
    if (!showFallback || fallbackTrackedRef.current) {
      return;
    }

    fallbackTrackedRef.current = true;
    trackRegistrationNoProviderFallback();
  }, [showFallback]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    if (!showFallback) {
      history.replace("/register/account");
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history, showFallback]);

  if (!showFallback) {
    return null;
  }

  const handleStartOver = () => {
    releaseLock();
    clearRegistrationData();
    history.push("/register");
  };

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            variant="welcome"
            stepMeta={WELCOME_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            showBack={false}
            showProgress={false}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card content-card-no-rd">
            <div className="content-card-scroll content-card-scroll-no-rd">
              <div className="no-rd-hero">
                <div className="no-rd-icon" aria-hidden>
                  🌱
                </div>
                <p
                  className="no-rd-message"
                  ref={stepTitleRef}
                  {...REGISTRATION_STEP_TITLE_PROPS}
                >
                  {t("DISCOVRY_QUEUE_LBL_DECS")}
                </p>
                {referenceNumber && (
                  <p className="no-rd-reference">
                    {t("NUTRITION_NORD_LBL_REFERENCE_NUMBER")}{" "}
                    <strong>#{referenceNumber}</strong>
                  </p>
                )}
                <div className="no-rd-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleStartOver}
                  >
                    {t("NUTRITION_NORD_BTN_START_OVER")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RegistrationLayout>
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationNoRdAvailable);
